import React, { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../i18n';
import { isPdf, DocThumb } from './MediaStrip';

// One line of a service request, edited on its own screen.
//
// The homeowner's half of what ItemEditor does for a technician's estimate, and
// deliberately the same screen: a name, the whole description with room to
// write it, and the photos that belong to THIS job rather than to the request
// as a whole.
//
// Why a screen and not another row in the form: a customer with three things
// wrong types three different amounts about them, and the third one is usually
// the longest. A list of one-line inputs quietly tells them to keep it short,
// which is the opposite of what an estimate needs.
//
// It takes PDFs as well as photos and video. A property manager's move-out
// inspection, a drawing, a quote from somebody else — those describe a job
// better than a snapshot does, and a form that refuses them just teaches
// people to email instead, where the request is no longer tracked. They ride
// the exact same path as a photo; only the thumbnail differs.
//
// The one real difference from ItemEditor is the photos, and it depends on who
// is holding the phone:
//
//   · The public request form has no account behind it, and /api/uploads needs
//     one. So the files are HELD here and travel with the request itself when
//     it is submitted — nothing is uploaded until they press Submit.
//   · Julian logging a lead from a phone call is signed in, so he passes
//     `onUploadFiles` and his photos go up as he picks them, exactly as they do
//     on a technician's estimate line. The line then carries URLs.
//
// Both shapes render the same, and an item can legitimately hold both — which
// is why `photos` and `files` are counted and listed together throughout.

export default function RequestItemEditor({ item, index, total, serviceTypes, onChange, onDelete, onClose, onUploadFiles }) {
  const { t, optLabel } = useI18n();
  const [busy, setBusy] = useState(false);
  const set = (patch) => onChange({ ...item, ...patch });
  // Memoised so an item with no photos yet doesn't hand the effect below a
  // brand new [] on every keystroke — that revokes and rebuilds every preview
  // while they are still typing the description.
  const files = useMemo(() => item.files || [], [item.files]);

  // Thumbnails for files that only exist in the browser. Revoked when the list
  // changes or the screen closes — a phone photo is several megabytes and this
  // screen is opened and closed repeatedly.
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    const urls = files.map(f => (f.type || '').startsWith('image/') ? URL.createObjectURL(f) : null);
    setPreviews(urls);
    return () => urls.forEach(u => u && URL.revokeObjectURL(u));
  }, [files]);

  // A file still sitting in the browser has no URL to read an extension off,
  // so it is asked directly. Android sometimes sends an empty `type` for a
  // file picked out of Downloads, hence the name as a fallback.
  const heldIsPdf = (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name || '');

  // Already-uploaded photos (the signed-in path), kept beside the held files.
  const photos = item.photos || [];

  const addFiles = async (picked) => {
    if (!picked.length) return;

    // Signed in: straight to Cloudinary, and the line carries the URL.
    if (onUploadFiles) {
      setBusy(true);
      try {
        const urls = await onUploadFiles(picked);
        if (urls?.length) set({ photos: [...photos, ...urls].slice(0, 25) });
      } finally {
        setBusy(false);
      }
      return;
    }

    const merged = [...files, ...picked.filter(f => !files.some(p => p.name === f.name && p.size === f.size))];
    if (merged.length + photos.length > 25) alert(t('req.max25'));
    set({ files: merged.slice(0, 25) });
  };

  return (
    <>
      <div className="modal active" style={{ alignItems: 'stretch' }}>
        <div className="item-editor">
          <div className="item-editor-bar">
            <button type="button" className="item-editor-back" onClick={onClose}>‹ {t('req.items')}</button>
            <span className="item-editor-n">{index + 1} / {total}</span>
            <button type="button" className="item-editor-done" onClick={onClose}>{t('ie.done')}</button>
          </div>

          <div className="item-editor-body">
            <div className="item-editor-sec">{t('req.itemDetails')}</div>

            {/* Their own words, and the label this job carries from here on —
                on the lead card, on the estimate, on the tech's job sheet. */}
            <input
              className="item-editor-name"
              value={item.title || ''}
              onChange={e => set({ title: e.target.value })}
              placeholder={t('req.serviceNeeded')}
            />

            <textarea
              className="item-editor-desc"
              value={item.description || ''}
              onChange={e => set({ description: e.target.value })}
              placeholder={t('req.describePh')}
              rows={6}
            />

            <div className="item-editor-row">
              <span>{t('req.serviceType')}</span>
              <select
                value={item.serviceType || ''}
                onChange={e => set({ serviceType: e.target.value })}
                style={{ border: 'none', background: 'transparent', font: 'inherit', color: 'var(--navy)', textAlign: 'right', maxWidth: '60%' }}
              >
                <option value="">{t('req.select')}</option>
                {serviceTypes.map(s => <option key={s} value={s}>{optLabel(s)}</option>)}
              </select>
            </div>

            <div className="item-editor-row">
              <span>{t('req.attachments')}</span>
              <span style={{ color: 'var(--muted)' }}>{files.length + photos.length}</span>
            </div>
            <div className="item-editor-hint">{t('req.itemPhotosHint')}</div>

            {(files.length > 0 || photos.length > 0) && (
              <div className="photo-strip" style={{ padding: '10px 16px' }}>
                {photos.map((url, i) => (
                  <div key={url} style={{ position: 'relative', flexShrink: 0 }}>
                    {isPdf(url)
                      ? <DocThumb onClick={() => window.open(url, '_blank')} title={url.split('/').pop()} />
                      : <img src={url} alt="" className="photo-thumb" onClick={() => window.open(url, '_blank')} />}
                    <button
                      type="button"
                      className="item-editor-x"
                      aria-label={t('req.cancel')}
                      onClick={() => set({ photos: photos.filter((_, idx) => idx !== i) })}
                    >×</button>
                  </div>
                ))}
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} style={{ position: 'relative', flexShrink: 0 }}>
                    {previews[i]
                      ? <img src={previews[i]} alt="" className="photo-thumb" />
                      : heldIsPdf(f)
                        ? <DocThumb title={f.name} />
                        : <div className="photo-thumb photo-thumb-video">▶</div>}
                    <button
                      type="button"
                      className="item-editor-x"
                      aria-label={t('req.cancel')}
                      onClick={() => set({ files: files.filter((_, idx) => idx !== i) })}
                    >×</button>
                  </div>
                ))}
              </div>
            )}

            {/* A label with the input inside it, per the Safari rule in
                PROJECT-STATUS: a scripted .click() on a hidden input opens
                nothing on an iPhone, and half these photos are taken on one. */}
            <label className="item-editor-add">
              {busy ? t('req.submitting') : t('req.addFiles')}
              <input
                type="file" accept="image/*,video/*,application/pdf,.pdf" multiple hidden disabled={busy}
                onChange={e => { addFiles(Array.from(e.target.files)); e.target.value = ''; }}
              />
            </label>

            <div style={{ padding: '22px 16px 40px' }}>
              {total > 1 && (
                <button type="button" className="btn btn-ghost btn-lg" style={{ color: 'var(--red)', borderColor: 'var(--red)' }} onClick={onDelete}>
                  {t('req.removeItem')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="overlay active" onClick={onClose}></div>
    </>
  );
}
